import React from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../contexts/AuthContext';
import { Card, Button } from '../ui';
import appointmentService from '../../services/AppointmentService';
import WorkOrderService from '../../services/WorkOrderService';

/**
 * CustomerInfo component displays and allows editing of a customer's basic information
 */
const CustomerInfo = ({ customer, onEdit, loading = false, editable = true }) => {
  const { hasPermission } = useAuth();
  const canEditCustomer = hasPermission('customers', 'write');

  // Format date to local string
  const formatDate = (dateString) => {
    if (!dateString) return '未记录';
    return new Date(dateString).toLocaleDateString('zh-CN');
  };

  // Grouped info for two-column layout
  const basicInfoItems = [
    {
      label: '姓名',
      value: customer.name || '未提供',
      icon: (
        <svg className="h-5 w-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      )
    },
    {
      label: '电话',
      value: customer.phoneNumber || '未提供',
      icon: (
        <svg className="h-5 w-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
      )
    },
    {
      label: '性别/年龄',
      value: `${customer.gender || '未知'}${customer.age ? ` / ${customer.age}岁` : ''}`,
      icon: (
        <svg className="h-5 w-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
    },
    {
      label: '分组',
      value: customer.ageGroup ? (
        customer.ageGroup === 'youth' ? '青少年' : customer.ageGroup === 'adult' ? '成年人' : '老年人'
      ) : '未提供',
      icon: (
        <svg className="h-5 w-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke="currentColor" strokeWidth="2" d="M17 20v-2a4 4 0 00-4-4H7a4 4 0 00-4 4v2" />
          <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" fill="none" />
          <path stroke="currentColor" strokeWidth="2" d="M23 20v-2a4 4 0 00-3-3.87" />
          <path stroke="currentColor" strokeWidth="2" d="M16 3.13a4 4 0 010 7.75" />
        </svg>
      )
    },
    {
      label: '身高',
      value: customer.height ? `${customer.height} cm` : '未提供',
      icon: (
        <svg className="h-5 w-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <rect x="7" y="3" width="10" height="18" rx="2" stroke="currentColor" strokeWidth="2" fill="none" />
          <path stroke="currentColor" strokeWidth="2" d="M10 7h4M10 11h4M10 15h4" />
        </svg>
      )
    },
    {
      label: '体重',
      value: customer.weight ? `${customer.weight} kg` : '未提供',
      icon: (
        <svg className="h-5 w-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" fill="none" />
          <path stroke="currentColor" strokeWidth="2" d="M12 7v5l3 3" />
        </svg>
      )
    },
    {
      label: '登记',
      value: formatDate(customer.createdAt),
      icon: (
        <svg className="h-5 w-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      )
    },
    {
      label: '类型',
      value: customer.customerType === 'returning' ? '老客户' : '新客户',
      icon: (
        <svg className="h-5 w-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      )
    }
  ];

  // 真实统计
  const totalAppointments = customer?.id ? appointmentService.getAppointmentsByCustomer(customer.id).length : 0;
  const totalTreatmentRecords = customer?.id ? WorkOrderService.getAllWorkOrders().filter(order => order.customerId === customer.id).length : 0;

  return (
    <Card variant="modern" className="border-emerald-100 h-fit md:max-w-xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-lg">
            {customer.name ? customer.name.charAt(0) : '?'}
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900">
              客户信息
            </h3>
            <p className="text-gray-600 text-sm">
              个人详情和联系方式
            </p>
          </div>
        </div>
        {editable && canEditCustomer && (
          <Button
            variant="secondary"
            size="small"
            onClick={onEdit}
            disabled={loading}
            loading={loading}
          >
            <svg className="h-5 w-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            编辑
          </Button>
        )}
      </div>

      {/* 基本信息 - 两列网格 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 mb-4">
        {basicInfoItems.map((item, index) => (
          <div key={index} className="flex items-center space-x-2 py-1">
            <span className="flex-shrink-0">{item.icon}</span>
            <span className="text-sm text-gray-600 font-medium">{item.label}：</span>
            <span className="text-sm text-gray-900 break-words">{item.value}</span>
          </div>
        ))}
      </div>

      {/* 健康档案 - 两列网格，分隔线 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 mt-4 pt-4 border-t border-gray-100">
        <div>
          <div className="text-sm text-gray-600 font-medium mb-1">备注信息</div>
          <div className="text-sm text-gray-900 bg-gray-50 rounded p-2 border border-gray-200 min-h-[32px]">
            {customer.notes ? (
              <span className="whitespace-pre-line">{customer.notes}</span>
            ) : (
              <span className="text-gray-400 italic">暂无备注信息</span>
            )}
          </div>
        </div>
        <div>
          <div className="text-sm text-gray-600 font-medium mb-1">生活习惯</div>
          <div className="text-sm text-gray-900 bg-gray-50 rounded p-2 border border-gray-200 min-h-[32px]">
            <div>饮食：{customer.lifestyle?.diet || '未提供'}</div>
            <div>作息：{customer.lifestyle?.sleep || '未提供'}</div>
            <div>运动：{customer.lifestyle?.exercise || '未提供'}</div>
          </div>
        </div>
        <div>
          <div className="text-sm text-gray-600 font-medium mb-1">家族病史</div>
          <div className="text-sm text-gray-900 bg-gray-50 rounded p-2 border border-gray-200 min-h-[32px]">
            {Array.isArray(customer.familyHistory)
              ? (customer.familyHistory.length > 0 ? customer.familyHistory.join(', ') : '未提供')
              : (customer.familyHistory || '未提供')}
          </div>
        </div>
        <div>
          <div className="text-sm text-gray-600 font-medium mb-1">当前主要健康问题</div>
          <div className="text-sm text-gray-900 bg-gray-50 rounded p-2 border border-gray-200 min-h-[32px]">
            {Array.isArray(customer.currentSymptoms)
              ? (customer.currentSymptoms.length > 0 ? customer.currentSymptoms.join(', ') : '未提供')
              : (customer.currentSymptoms || '未提供')}
          </div>
        </div>
        <div>
          <div className="text-sm text-gray-600 font-medium mb-1">意向调理项目</div>
          <div className="text-sm text-gray-900 bg-gray-50 rounded p-2 border border-gray-200 min-h-[32px]">
            {customer.intendedProject || '未提供'}
          </div>
        </div>
        <div>
          <div className="text-sm text-gray-600 font-medium mb-1">意向调理师</div>
          <div className="text-sm text-gray-900 bg-gray-50 rounded p-2 border border-gray-200 min-h-[32px]">
            {customer.intendedPractitioner || '未提供'}
          </div>
        </div>
        <div>
          <div className="text-sm text-gray-600 font-medium mb-1">特殊需求</div>
          <div className="text-sm text-gray-900 bg-gray-50 rounded p-2 border border-gray-200 min-h-[32px]">
            {customer.specialNeeds || '未提供'}
          </div>
        </div>
      </div>

      {/* 地址单独一行，分组间距 */}
      <div className="flex items-center space-x-2 mt-4">
        <span className="text-sm text-gray-600 font-medium">地址：</span>
        <span className="text-sm text-gray-900 break-words">{customer.address || '未提供'}</span>
      </div>

      {/* Quick Stats */}
      <div className="mt-6 pt-6 border-t border-gray-100">
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center p-3 bg-emerald-50 rounded-lg border border-emerald-200">
            <div className="text-lg font-semibold text-emerald-700">
              {totalAppointments}
            </div>
            <div className="text-xs text-emerald-600 font-medium">
              总预约次数
            </div>
          </div>
          <div className="text-center p-3 bg-emerald-50 rounded-lg border border-emerald-200">
            <div className="text-lg font-semibold text-emerald-700">
              {totalTreatmentRecords}
            </div>
            <div className="text-xs text-emerald-600 font-medium">
              治疗记录数
            </div>
          </div>
        </div>
      </div>


    </Card>
  );
};

CustomerInfo.propTypes = {
  customer: PropTypes.object.isRequired,
  onEdit: PropTypes.func,
  loading: PropTypes.bool,
  editable: PropTypes.bool,
};

export default CustomerInfo;